import type { PlayEvent, RacquetSportId } from '@court-sync/shared';
import { BrowserImage } from '@/components/ui/BrowserImage';
import { SportIconTile } from '@/components/sport/SportIcons';
import { cn } from '@/lib/utils';

export type PublicPlayEventCoverImageProps = {
  event: Pick<PlayEvent, 'coverImageUrl' | 'name' | 'sportId'>;
  className?: string;
  /** When true, uses a shorter aspect ratio suited to card headers. */
  compact?: boolean;
  /** Fixed-height hero for modals and detail headers (object-cover fill). */
  hero?: boolean;
  /** Fill the parent container (parent must set size / aspect). */
  fill?: boolean;
};

function PlayEventCoverPlaceholder({
  name,
  sportId,
  className,
}: {
  name: string;
  sportId: RacquetSportId;
  className?: string;
}) {
  const monogram = name.trim().charAt(0).toUpperCase() || '?';

  return (
    <div
      className={cn(
        'relative flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 via-primary/10 to-muted',
        className
      )}
      aria-hidden
    >
      <SportIconTile
        sportId={sportId}
        className="absolute right-4 top-4 h-8 w-8 text-primary/30"
      />
      <span className="select-none text-3xl font-bold tracking-tight text-primary/80 sm:text-4xl">
        {monogram}
      </span>
    </div>
  );
}

/**
 * Cover image for public play event cards and detail views.
 */
export function PublicPlayEventCoverImage({
  event,
  className,
  compact = false,
  hero = false,
  fill = false,
}: PublicPlayEventCoverImageProps) {
  const containerClass = cn(
    'overflow-hidden bg-muted',
    fill
      ? 'h-full w-full'
      : cn(
          'w-full',
          hero
            ? 'h-40 sm:h-44'
            : compact
              ? 'aspect-[2/1] sm:aspect-[5/2]'
              : 'aspect-[16/9] sm:aspect-[21/9]'
        ),
    className
  );

  const placeholder = (
    <PlayEventCoverPlaceholder
      name={event.name}
      sportId={event.sportId}
      className={containerClass}
    />
  );

  const src = event.coverImageUrl?.trim();
  if (!src) {
    return placeholder;
  }

  return (
    <div className={containerClass}>
      <BrowserImage
        src={src}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover"
        fallback={placeholder}
      />
    </div>
  );
}
